import React, { useEffect, useState } from "react";
import { Admin, CustomRoutes } from "react-admin";
import { Route } from "react-router-dom";
import { Box, CircularProgress, CssBaseline, Typography } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { FirebaseAuthProvider, FirebaseDataProvider } from "react-admin-firebase";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import LoginPage from "./LoginPage";
import SupplierLayout from "./SupplierLayout";
import SupplierDashboard from "./SupplierDashboard";
import SupplierBookingsPage from "./SupplierBookingsPage";
import SupplierDriversPage from "./SupplierDriversPage";
import supplierTheme from "./theme";
import logo from "./assets/comfort-logo.png";
import { firebaseConfig } from "./firebaseClient";

if (!firebase.apps.length) firebase.initializeApp(firebaseConfig);

const options = {
  lazyLoading: { enabled: true },
};

const dataProvider = FirebaseDataProvider(firebaseConfig, options);
const baseAuthProvider = FirebaseAuthProvider(firebaseConfig, options);

// react-admin-firebase may treat Firestore permission/index errors as an auth failure.
// In this portal those are data-page errors; do not destroy a valid Firebase session.
const authProvider = {
  ...baseAuthProvider,
  checkError: async (error) => {
    const code = String(error?.code || "").toLowerCase();
    const message = String(error?.message || "").toLowerCase();
    const status = Number(error?.status || 0);
    const isDataAccessError =
      code.includes("permission-denied") ||
      code.includes("failed-precondition") ||
      message.includes("missing or insufficient permissions") ||
      message.includes("query requires an index") ||
      status === 403;

    if (isDataAccessError) return Promise.resolve();
    return baseAuthProvider.checkError(error);
  },
};

function useFirebaseSession() {
  const [state, setState] = useState({ loading: true, user: null });
  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
      setState({ loading: false, user: user || null });
    });
    return unsubscribe;
  }, []);
  return state;
}

function LoadingScreen() {
  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "background.default" }}>
      <Box sx={{ textAlign: "center" }}>
        <Box component="img" src={logo} alt="Comfort Line Luxury" sx={{ width: 150, mb: 2 }} />
        <CircularProgress size={30} />
        <Typography color="text.secondary" sx={{ mt: 1.5 }}>Loading Supplier Portal...</Typography>
      </Box>
    </Box>
  );
}

export default function App() {
  const { loading, user } = useFirebaseSession();
  const authenticatedLoginRoute = !loading && user && window.location.hash.startsWith("#/login");

  useEffect(() => {
    if (authenticatedLoginRoute) {
      window.location.hash = "#/";
    }
  }, [authenticatedLoginRoute]);

  if (loading || authenticatedLoginRoute) return (
    <ThemeProvider theme={supplierTheme}>
      <CssBaseline />
      <LoadingScreen />
    </ThemeProvider>
  );
  if (!user) return (
    <ThemeProvider theme={supplierTheme}>
      <CssBaseline />
      <LoginPage />
    </ThemeProvider>
  );

  return (
    <Admin
      dataProvider={dataProvider}
      authProvider={authProvider}
      dashboard={SupplierDashboard}
      layout={SupplierLayout}
      loginPage={LoginPage}
      theme={supplierTheme}
      title="Comfort Line Supplier Portal"
    >
      <CustomRoutes>
        <Route path="/new-bookings" element={<SupplierBookingsPage mode="new" />} />
        <Route path="/accepted-bookings" element={<SupplierBookingsPage mode="accepted" />} />
        <Route path="/active-rides" element={<SupplierBookingsPage mode="active" />} />
        <Route path="/completed-rides" element={<SupplierBookingsPage mode="completed" />} />
        <Route path="/my-drivers" element={<SupplierDriversPage />} />
      </CustomRoutes>
    </Admin>
  );
}
